import React from 'react';
import { Row, Col, FlexView, COLORS, Separator, Button } from '@cuemath/comps';
import styled from 'styled-components';

import BookIcon from 'img/book.svg';
import MonitorIcon from 'img/monitor.svg';
import { CopyButton } from 'comps';
import Tooltip from 'comps/tooltip';
import {
  POCHeading,
  ChipLogo,
  ChipText,
  ChipView,
  ChipViewWrapper,
  POCValue,
  POCWrapper,
} from 'screens/student-details/styled-comps';
import { convertEpochToDateFormat } from 'utils';

import ActiveStatusIndicator from 'comps/student-statusI-indicator';
import { useStudentGet } from 'actions/studentsv2';
import {
  useStudentsGetOfParent,
  useToggleStripe,
  useParentOpportunityCountryUpdate,
} from 'actions/parentsv2';
import Select from 'react-select';
import { COUNTRY_OPTIONS_V2 } from 'utils';
import { useEffect } from 'react';

const Wrapper = styled.div`
  padding: 14px;
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 8px;
  margin-top: 12px;
`;

const CustomRow = styled(Row)`
  margin-top: ${({ marginTop }) => (marginTop ? marginTop : '20px')};
`;

const StyledChipView = styled(ChipView)`
  background: ${COLORS.GRAY_XXXLIGHT};
`;

const ToggleButton = styled(Button)`
  background-color: ${({ isEnabled }) => (isEnabled ? COLORS.GREEN : COLORS.RED)};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    background-color: ${({ isEnabled, disabled }) =>
      disabled
        ? isEnabled
          ? COLORS.GREEN
          : COLORS.RED
        : isEnabled
        ? COLORS.GREEN_DARK || COLORS.GREEN
        : COLORS.RED_DARK || COLORS.RED};
  }
`;

const UpdateCountryButton = styled(Button)`
  width: fit-content;
  padding: 16px 24px;

  & > div {
    text-align: center;
  }
`;

export default function ParentInfo({ parentServiceId, studentId }) {
  const { data: studentDetails } = useStudentGet(studentId);
  const { data: studentsOfParent, get: getStudentsOfParent } = useStudentsGetOfParent(
    parentServiceId,
    {
      include_primary_course: true,
      include_primary_teacher: true,
      include_stripe_feature_details: true,
      include_opportunity_country_update_details: true,
    },
  );

  const { patch: toggleStripe, loading: toggleLoading } = useToggleStripe();

  const {
    students,
    is_stripe_enabled_for_parent: isStripeEnabled,
    is_user_allowed_to_toggle_stripe: canToggleStripe,
    is_user_allowed_to_update_country: isCountryUpdateAllowed,
  } = studentsOfParent || {};
  const { parent_data: parentData, country, parent_name: parentName } =
    studentDetails || {};

  const {
    ref_code: refCode,
    primary_user_id: primaryUserId,
    signup_code: signupCode,
    source: source,
    created_by_id: createdById,
    detail_source: detailSource,
    region_data: regionData,
  } = parentData || {};

  const { opportunity_country: opportunityCountry } = regionData || {};

  const [selectedCountry, setSelectedCountry] = React.useState(
    opportunityCountry
      ? COUNTRY_OPTIONS_V2.find(opt => opt.value === opportunityCountry)
      : null,
  );
  const [countryDirty, setCountryDirty] = React.useState(false);
  const [countryError, setCountryError] = React.useState('');
  const [countrySuccess, setCountrySuccess] = React.useState(false);
  const [updateReason, setUpdateReason] = React.useState('');
  const {
    patch: updateOpportunityCountry,
    loading: updateCountryLoading,
  } = useParentOpportunityCountryUpdate();

  const handleCountryChange = option => {
    setSelectedCountry(option);
    setCountryDirty(true);
    setCountryError('');
  };

  const handleCountryUpdate = async () => {
    if (!updateReason.trim()) {
      setCountryError('Please provide a reason for updating the country.');
      return;
    }
    try {
      await updateOpportunityCountry(parentServiceId, {
        opportunity_country: selectedCountry.id,
        force_update: true,
        update_reason: `Leap Admin: ${updateReason.trim()}`,
      });
      setCountryDirty(false);
      setCountryError('');
      setCountrySuccess(true);
      setUpdateReason('');
      setTimeout(() => setCountrySuccess(false), 5000);
    } catch (error) {
      setCountryError('Failed to update country. Please try again.');
      setCountrySuccess(false);
      console.error('Error updating opportunity country:', error);
    }
  };

  const studentListCount = students && students.length;
  const referralLink = refCode
    ? `https://www.cuemath.com/gift/referral/${refCode}/?utm_source=la`
    : '';

  const handleToggleStripe = async () => {
    try {
      await toggleStripe(parentServiceId);
      // Refresh the data after successful toggle
      getStudentsOfParent(parentServiceId, {
        include_primary_course: true,
        include_primary_teacher: true,
        include_stripe_feature_details: true,
        include_opportunity_country_update_details: true,
      });
    } catch (error) {
      console.error('Error toggling stripe status:', error);
    }
  };

  useEffect(() => {
    getStudentsOfParent(parentServiceId, {
      include_primary_course: true,
      include_primary_teacher: true,
      include_stripe_feature_details: true,
      include_opportunity_country_update_details: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <POCWrapper>
      <Row>
        <POCHeading variant="eyebrow2">Parent</POCHeading>
        <POCValue variant="cta1">{parentName || '-'}</POCValue>
      </Row>
      <Row>
        <POCHeading variant="eyebrow2">Phone Country</POCHeading>
        <POCValue variant="cta1">{country || '-'}</POCValue>
      </Row>
      <Row>
        <POCHeading variant="eyebrow2">Opportunity Country</POCHeading>
        <div style={{ minWidth: 340 }}>
          <Select
            options={COUNTRY_OPTIONS_V2}
            value={selectedCountry}
            onChange={isCountryUpdateAllowed ? handleCountryChange : undefined}
            isSearchable
            placeholder={selectedCountry ? selectedCountry.label : 'Assign Country'}
            isDisabled={updateCountryLoading || !isCountryUpdateAllowed}
            styles={{
              control: base => ({ ...base, minHeight: 36, width: 320 }),
              menu: base => ({ ...base, zIndex: 9999 }),
            }}
          />
          {countryDirty && isCountryUpdateAllowed && (
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <input
                  type="text"
                  value={updateReason}
                  onChange={e => setUpdateReason(e.target.value)}
                  placeholder="Enter update reason"
                  style={{
                    width: 320,
                    padding: 8,
                    borderRadius: 4,
                    border: '1px solid #ccc',
                  }}
                  maxLength={200}
                  disabled={updateCountryLoading}
                />
              </div>
              <UpdateCountryButton
                label="Update Country"
                disabled={
                  updateCountryLoading || !selectedCountry || !updateReason.trim()
                }
                onClick={handleCountryUpdate}
              />
            </div>
          )}
          {countryError && (
            <div style={{ color: 'red', marginTop: 8 }}>{countryError}</div>
          )}
          {countrySuccess && (
            <div style={{ color: 'green', marginTop: 8 }}>
              Country updated successfully!
            </div>
          )}
        </div>
        <div style={{ height: 16 }} />
      </Row>
      <Row>
        <POCHeading variant="eyebrow2">Signup code</POCHeading>
        <POCValue variant="cta1">{signupCode || '-'}</POCValue>
      </Row>
      <Row>
        <POCHeading variant="eyebrow2">Detail source</POCHeading>
        <POCValue variant="cta1">{detailSource || '-'}</POCValue>
      </Row>
      <Row>
        <POCHeading variant="eyebrow2">Source</POCHeading>
        <POCValue variant="cta1">{source || '-'}</POCValue>
      </Row>
      <Row>
        <POCHeading variant="eyebrow2">Primary user id</POCHeading>
        <POCValue variant="cta1">{primaryUserId || '-'}</POCValue>
      </Row>
      <Row>
        <POCHeading variant="eyebrow2">Created by id</POCHeading>
        <POCValue variant="cta1">{createdById || '-'}</POCValue>
      </Row>
      <CustomRow>
        <POCHeading variant="eyebrow2">Referral link</POCHeading>
      </CustomRow>
      <CustomRow marginTop="4px">
        <CopyButton copyMessage="Copy Referral Link" text={referralLink || '-'} />
      </CustomRow>
      <CustomRow>
        <POCHeading variant="eyebrow2">Stripe status</POCHeading>
      </CustomRow>
      <CustomRow marginTop="4px">
        <FlexView flexDirection="row" alignItems="center">
          {!canToggleStripe ? (
            <Tooltip
              title="You are not allowed to toggle stripe PG"
              position="top"
              width="250px"
            >
              <ToggleButton
                isEnabled={isStripeEnabled}
                disabled={!canToggleStripe || toggleLoading}
              >
                {isStripeEnabled ? 'Enabled' : 'Disabled'}
              </ToggleButton>
            </Tooltip>
          ) : (
            <ToggleButton
              isEnabled={isStripeEnabled}
              disabled={!canToggleStripe || toggleLoading}
              onClick={handleToggleStripe}
            >
              {toggleLoading ? 'Updating...' : isStripeEnabled ? 'Enabled' : 'Disabled'}
            </ToggleButton>
          )}
          <POCValue variant="cta1" style={{ marginLeft: '12px' }}>
            {isStripeEnabled ? 'Enabled' : 'Disabled'}
          </POCValue>
        </FlexView>
      </CustomRow>
      <CustomRow>
        <POCHeading variant="eyebrow2">Students ({studentListCount || '-'})</POCHeading>
      </CustomRow>
      {studentListCount > 0 &&
        students.map(
          ({
            first_name: firstName,
            last_name: lastName,
            grade,
            id,
            intel_enrollment_id: ieId,
            primary_course: primaryCourse,
            primary_teacher_details: primaryTeacherDetails,
          }) => {
            const { first_name: firstNameTeacher, last_name: lastNameTeacher } =
              primaryTeacherDetails || {};
            const {
              state,
              enrollment_type: enrollmentType,
              classes_balance: classesBalance,
              classes_purchased: classesPurchased,
              validity_till_date: validityTillDate,
              class_ratio: classRatio,
            } = primaryCourse || {};

            const formattedValidityTillDate = validityTillDate
              ? convertEpochToDateFormat(validityTillDate * 1000)
              : '-';
            return (
              <Wrapper>
                <Row justifyContent="space-between" alignItems="flex-start">
                  <POCHeading variant="eyebrow1">
                    {firstName || '-'} {lastName || '-'}
                  </POCHeading>

                  <ActiveStatusIndicator status={state || '-'} />
                </Row>
                <FlexView flexDirection="row" alignItems="center">
                  <StyledChipView
                    alignItems="center"
                    justifyContent="flex-start"
                    marginRight="6px"
                  >
                    <ChipLogo src={BookIcon} />
                    <ChipText variant="body2">Grade {grade || '-'}</ChipText>
                  </StyledChipView>
                  <StyledChipView alignItems="center" justifyContent="flex-start">
                    <ChipLogo src={MonitorIcon} />
                    <ChipText variant="body2">
                      {enrollmentType ? `${enrollmentType} |  1:${classRatio}` : '-'}
                    </ChipText>
                  </StyledChipView>
                </FlexView>
                <POCWrapper>
                  <Row>
                    <Col gutter={0}>
                      <POCHeading width="120px" variant="eyebrow2">
                        Teacher
                      </POCHeading>
                    </Col>
                    <Col gutter={0}>
                      <POCValue variant="cta1">
                        {firstNameTeacher
                          ? `${firstNameTeacher}\n${lastNameTeacher}`
                          : '-'}
                      </POCValue>
                      <Separator height={12} transparent />
                    </Col>
                  </Row>
                  <Row>
                    <POCHeading width="120px" variant="eyebrow2">
                      Class Balance
                    </POCHeading>
                    <POCValue variant="cta1">
                      {classesBalance ? `${classesBalance} / ${classesPurchased}` : '-'}
                    </POCValue>
                  </Row>
                  <Row>
                    <POCHeading width="122px" variant="eyebrow2">
                      TENURE END DATE
                    </POCHeading>
                    <POCValue variant="cta1">{formattedValidityTillDate}</POCValue>
                  </Row>
                  <Row>
                    <POCHeading width="122px" variant="eyebrow2">
                      Student id
                    </POCHeading>
                    <POCValue variant="cta1">{id}</POCValue>
                  </Row>
                  <Row>
                    <POCHeading width="122px" variant="eyebrow2">
                      Intelenrollment id
                    </POCHeading>
                    <POCValue variant="cta1">{ieId}</POCValue>
                  </Row>
                </POCWrapper>
              </Wrapper>
            );
          },
        )}
    </POCWrapper>
  );
}
